/**
 * Odds Conversion Utility
 * Converts decimal odds to american or fractional format
 */

/**
 * Convert decimal odds to the specified format
 * @param {number} decimalOdds - The odds in decimal format (e.g., 2.50)
 * @param {string} format - Target format: 'decimal', 'american', or 'fractional'
 * @returns {string} Formatted odds string
 */
export const convertOdds = (decimalOdds, format = 'decimal') => {
  if (!decimalOdds || isNaN(decimalOdds)) return '—'

  const odds = parseFloat(decimalOdds)

  switch (format) {
    case 'american':
      return toAmerican(odds)
    case 'fractional':
      return toFractional(odds)
    case 'decimal':
    default:
      return odds.toFixed(2)
  }
}

/**
 * Convert decimal to American odds
 * @param {number} decimal - Decimal odds
 * @returns {string} American odds (e.g., "+150" or "-110")
 */
const toAmerican = (decimal) => {
  if (decimal >= 2.0) {
    // Positive American odds
    const american = Math.round((decimal - 1) * 100)
    return `+${american}`
  } else {
    // Negative American odds
    const american = Math.round(-100 / (decimal - 1))
    return `${american}`
  }
}

/**
 * Convert decimal to Fractional odds
 * @param {number} decimal - Decimal odds
 * @returns {string} Fractional odds (e.g., "3/2" or "1/2")
 */
const toFractional = (decimal) => {
  const numerator = decimal - 1

  // Find a nice fraction representation
  const tolerance = 0.01
  let bestNumerator = 1
  let bestDenominator = 1
  let bestError = Math.abs(numerator - 1)

  // Try common denominators
  for (let denom = 1; denom <= 100; denom++) {
    const numer = Math.round(numerator * denom)
    const error = Math.abs(numerator - numer / denom)
    if (error < bestError) {
      bestError = error
      bestNumerator = numer
      bestDenominator = denom
      if (error < tolerance) break
    }
  }

  // Simplify the fraction
  const gcd = (a, b) => b === 0 ? a : gcd(b, a % b)
  const divisor = gcd(bestNumerator, bestDenominator)

  return `${bestNumerator / divisor}/${bestDenominator / divisor}`
}

export default convertOdds
